// eslint-disable-next-line max-classes-per-file
import { inject, Injectable } from '@angular/core';
import {} from '@angular/core/rxjs-interop';
import { Params } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, firstValueFrom, map, of, shareReplay, switchMap } from 'rxjs';

import { SipalWorkPackageCreateDialogComponent } from './sipal-work-package-create-dialog/sipal-work-package-create-dialog.component';
import { SipalAsset, SipalCorrettivaRecord } from './sipal.model';
import { WorkPackageSortViewEnum } from '../pages/work-package-list-page/work-package-sort-view.enum';
import { AuthenticationService } from '../services/authentication.service';
import { CurrentConfigurationsService } from '../services/current-configuration.service';
import { CurrentProjectService, CustomProjectConfiguration } from '../services/current-project.service';
import { GroupsService } from '../services/groups.service';
import { WorkPackageStatusService } from '../services/work-package-status.service';
import { WorkPackagesService } from '../services/work-package.service';
import { ColorMap, Section } from '../utils';
import { DashboardWorkPackageList } from '../widgets/dashboard-work-package-list/dashboard-work-package-list';
import { SidebarItem } from '../widgets/sidebar-item/sidebar-item.component';
import { SimpleTreeManager } from '../widgets/wp-tree-view/simple-tree-manager';
import { TreeBuilderStrategy } from '../widgets/wp-tree-view/tree-core/tree-builder-strategy';
import { TreeUIFacade } from '../widgets/wp-tree-view/tree-core/tree-ui-facade';
import { WpClientDrivenTreeFacade } from '../widgets/wp-tree-view/wp-client-driven-tree-facade';
import { WorkPackageTreeNode } from '../widgets/wp-tree-view/wp-tree-view.component';
import { BaseWorkPackageDto, ProjectDto, WorkPackagePriorityDto } from '@api/backend-connector/models';
import { FormDefinitionsApi } from '@api/backend-connector/services';

const guastiConfigurationKey = 'guasti';
const assetConfigurationKey = 'assets';
const correttiveConfigurationKey = 'correttive';
const correttiveSKConfigurationKey = 'skCorrettiva';
// The canonical key of the correttiva form definition
const correttivaFormDefinitionKey = 'CORRETTIVA';

export const extractWpAssetId = (wp: Pick<BaseWorkPackageDto, 'customFields'>): string => wp.customFields.asset;

@Injectable({ providedIn: 'root' })
export class SipalService implements CustomProjectConfiguration {
  private readonly workPackagesService = inject(WorkPackagesService);
  private readonly workPackageStatusService = inject(WorkPackageStatusService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly groupsService = inject(GroupsService);
  private readonly currentConfigurationsService = inject(CurrentConfigurationsService);
  private readonly currentProjectService = inject(CurrentProjectService);
  private readonly formDefinitionsApi = inject(FormDefinitionsApi);

  readonly isMaintainer$ = this.authenticationService.getMe().pipe(
    switchMap((user) =>
      this.groupsService.listGroups({ offset: 0, limit: 0, canAssignWorkPackage: true, userId: user.id }),
    ),
    map(({ count }) => !!count),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  constructor() {
    this.currentProjectService.registerCustomConfiguration(this);
  }
  getPriority(): number {
    this;
    return 1;
  }

  readonly assets$ = this.currentConfigurationsService
    .getCurrentConfiguration()
    .pipe(map((configuration) => (configuration.data?.['sipal']?.[assetConfigurationKey] ?? []) as SipalAsset[]));

  readonly assetsMap$ = this.assets$.pipe(map((assets) => new Map(assets.map((a) => [a.guid, a]))));

  readonly guastiMap$ = this.currentConfigurationsService
    .getCurrentConfiguration()
    .pipe(
      map(
        (configuration) =>
          new Map(Object.entries(configuration?.data?.['sipal']?.[guastiConfigurationKey] ?? [])) as Map<
          string,
          string[]
          >,
      ),
    );

  /**
   * The correttiva form definition, when the tenant has one active and published: a corrective
   * work package is then pinned to it. Until the legacy migration creates it, corrective work
   * packages keep copying skCorrettiva from the configuration (correttivaSK$).
   */
  readonly correttivaFormDefinitionId$ = this.formDefinitionsApi
    .listFormDefinitions({ key: correttivaFormDefinitionKey, isActive: true, limit: 1 })
    .pipe(
      map((page) => page.items.find((definition) => !!definition.publishedVersion)?.id),
      catchError(() => of(undefined)),
      shareReplay({ refCount: false, bufferSize: 1 }),
    );

  readonly correttivaSK$ = this.currentConfigurationsService
    .getCurrentConfiguration()
    .pipe(map((configuration) => (configuration.data?.['sipal']?.[correttiveSKConfigurationKey] ?? []) as unknown));

  readonly correttiveRecords$ = this.currentConfigurationsService
    .getCurrentConfiguration()
    .pipe(
      map(
        (configuration) =>
          (configuration.data?.['sipal']?.[correttiveConfigurationKey] ?? []) as SipalCorrettivaRecord[],
      ),
    );

  readonly correttiveRecordsMap$ = this.correttiveRecords$.pipe(
    map((records) => new Map(records.map((record) => [record.assetGuid, record]))),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  async getAsset(assetId: string) {
    const assetsMap = await firstValueFrom(this.assetsMap$);
    const asset = assetsMap.get(assetId);
    return asset;
  }

  async getGuasti(assetId?: string) {
    if (!assetId) {
      return [];
    }
    const assetsMap = await firstValueFrom(this.assetsMap$);
    const guastiMap = await firstValueFrom(this.guastiMap$);
    const asset = assetsMap.get(assetId);
    if (!asset) {
      return [];
    }
    const descrizioneLivello2 = asset.guid.split('::')[1].trim();
    return guastiMap.get(descrizioneLivello2) ?? ['ALTRO'];
  }

  async getAllGuasti() {
    const guastiMap = await firstValueFrom(this.guastiMap$);
    return Array.from(guastiMap.values()).flatMap((a) => a);
  }

  async canHandle(projectId: string): Promise<boolean> {
    const currentProject = await firstValueFrom(this.currentProjectService.currentProject$);
    if (!currentProject || currentProject?.id !== projectId) {
      throw new Error('The current project is different or not set');
    }
    return currentProject.data?.projectType === 'correttive' || currentProject.data?.projectType === 'programmate';
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  async ganttEditable(projectId: string): Promise<boolean> {
    this;
    return false;
  }

  // SIPAL tasks are not planned against each other: no dependencies
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  async allowDependencies(projectId: string): Promise<boolean> {
    this;
    return false;
  }

  // The corrective tasks have no deadline worth a list: the latest ones are shown instead
  async dashboardWorkPackageLists(): Promise<DashboardWorkPackageList[]> {
    const currentProject = await firstValueFrom(this.currentProjectService.currentProject$);
    return currentProject?.data?.projectType === 'correttive'
      ? ['assignedToMe', 'latest']
      : ['assignedToMe', 'expiring'];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  async allowHierarchy(projectId: string): Promise<boolean> {
    this;
    return false;
  }

  async getTreeUIFacade(projectId: string): Promise<TreeUIFacade<WorkPackageTreeNode>> {
    return new WpClientDrivenTreeFacade(
      projectId,
      this.workPackagesService,
      new SipalGanttBuilderStrategy(this, this.workPackageStatusService),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  async tasksInitialView(projectId: string): Promise<Params | null | undefined> {
    const isMaintainer = await firstValueFrom(this.isMaintainer$);
    if (isMaintainer) {
      const openStatuses = (await firstValueFrom(this.workPackageStatusService.allActiveWorkPackageStatuses())).filter(
        (s) => !s.isWorkPackageClosed,
      );
      return {
        sort: WorkPackageSortViewEnum.Expiration,
        statusIds: openStatuses.map((s) => s.id),
      };
    } else {
      return {
        sort: WorkPackageSortViewEnum.Newest,
      };
    }
  }

  openCreateWorkPackageDialog(
    dialog: DialogService,
    project: ProjectDto,
    priorities: WorkPackagePriorityDto[],
    parent?: BaseWorkPackageDto,
  ): Promise<BaseWorkPackageDto | undefined> {
    this;
    return SipalWorkPackageCreateDialogComponent.open(dialog, project, priorities, parent);
  }

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  async genericWorkPackagesFilters(projectId: string): Promise<SidebarItem[]> {
    this;
    return [
      {
        label: 'i18n.WorkPackageListPage.View.assignedToMe',
        id: 'assigned-to-me',
        icon: 'person-pin',
      },
    ];
  }

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  async workPackagesSortOptions(projectId: string): Promise<Section[]> {
    this;
    return [
      {
        label: 'i18n.WorkPackageListPage.View.Sort.byNewest',
        id: WorkPackageSortViewEnum.Newest,
        icon: 'sort-down',
      },
      {
        label: 'i18n.WorkPackageListPage.View.expiring',
        id: WorkPackageSortViewEnum.Expiration,
        icon: 'hourglass',
      },
      {
        label: 'i18n.WorkPackageListPage.View.Sort.byName',
        id: 'name',
        icon: 'sort-alphabet',
      },
    ];
  }
}

class SipalGanttBuilderStrategy implements TreeBuilderStrategy<WorkPackageTreeNode> {
  constructor(
    private readonly sipalService: SipalService,
    private readonly workPackageStatusService: WorkPackageStatusService,
  ) {}

  private async extractInfo(node: WorkPackageTreeNode): Promise<{ impianto?: string; ubicazione?: string }> {
    const assetId = extractWpAssetId(node);
    if (!assetId) {
      return { impianto: undefined, ubicazione: undefined };
    }
    const correttivaRecord = (await firstValueFrom(this.sipalService.correttiveRecordsMap$)).get(assetId);
    if (!correttivaRecord) {
      return { impianto: undefined, ubicazione: undefined };
    }
    return {
      impianto: correttivaRecord['IMPIANTO'],
      ubicazione: correttivaRecord['LOCALIZZAZIONE'],
    };
  }

  private createFakeNode(id: string, name: string): WorkPackageTreeNode {
    this;
    return {
      id,
      name,
      createdAt: new Date().toISOString(),
      workPackageType: {
        name: '',
        color: {
          text: '',
        },
        icon: '',
      },
      workPackageStatus: {
        name: '',
        color: {
          text: '',
        },
        icon: '',
      },
      workPackagePriority: {
        name: '',
        color: {
          text: '',
        },
        icon: '',
      },
      code: '',
      workPackageDependenciesFrom: [],
      isFakeGroup: true,
    } as unknown as WorkPackageTreeNode;
  }

  async build(
    rawData: Map<string, WorkPackageTreeNode>,
    treeManager: SimpleTreeManager<WorkPackageTreeNode>,
  ): Promise<void> {
    // 1. VALIDAZIONE STRICT DEL DATASET
    // Nessun nodo proveniente dal backend deve avere un parentId valorizzato.
    for (const node of rawData.values()) {
      if (node.parentId) {
        throw new Error(
          // eslint-disable-next-line max-len
          `Inconsistent Dataset: Node ${node.id} has a parent (${node.parentId}). A perfectly flat hierarchy is expected for this strategy.`,
        );
      }
    }

    // Color hack
    const statusesMap = new Map(
      (await firstValueFrom(this.workPackageStatusService.allWorkPackageStatuses())).map((s) => [s.id, s]),
    );
    const now = new Date();
    const oneMonthFromNow = new Date();
    oneMonthFromNow.setMonth(now.getMonth() + 1);
    for (const node of rawData.values()) {
      const getColor = () => {
        // closed (no override)
        if (statusesMap.get(node.workPackageStatus.id)?.isWorkPackageClosed) {
          return node.workPackageType.color;
        }
        // expired
        if (node.isExpired) {
          return ColorMap.red;
        }
        const expiration = node.endDate ? new Date(node.endDate) : undefined;
        // expiring
        if (expiration && expiration > now && expiration <= oneMonthFromNow) {
          return ColorMap.yellow;
        }
        return node.workPackageType.color;
      };

      node.ganttRowCustomColor = getColor();
    }

    // Strutture dati per lo smistamento (non serve più mappare le relazioni)
    const esazioneRoots = new Map<string, WorkPackageTreeNode[]>();
    const standardNodes: WorkPackageTreeNode[] = [];

    // 2. SMISTAMENTO DEI NODI (Flat)
    for (const node of rawData.values()) {
      const { impianto, ubicazione } = await this.extractInfo(node);
      if (impianto === 'ESAZIONE') {
        const ubiKey = ubicazione!;
        if (!esazioneRoots.has(ubiKey)) {
          esazioneRoots.set(ubiKey, []);
        }
        esazioneRoots.get(ubiKey)!.push(node);
      } else {
        standardNodes.push(node);
      }
    }

    let rootGlobalIndex = 0;

    // 3. COSTRUZIONE STRUTTURA FITTIZIA ESAZIONE (Primo blocco, in cima)
    if (esazioneRoots.size > 0) {
      const rootEsazioneId = 'GROUP_ESAZIONE';

      // Nodo radice "ESAZIONE"
      treeManager.addNode(
        rootEsazioneId,
        this.createFakeNode(rootEsazioneId, 'ESAZIONE'),
        undefined,
        rootGlobalIndex++,
      );

      let ubiIndex = 0;
      for (const [ubicazione, nodes] of esazioneRoots.entries()) {
        const ubiId = `GROUP_UBI_${ubicazione}`;

        // Sotto-nodi "Ubicazione"
        treeManager.addNode(ubiId, this.createFakeNode(ubiId, ubicazione), rootEsazioneId, ubiIndex++);

        // Ordina e inserisci i veri nodi come "foglie" dell'ubicazione
        nodes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        nodes.forEach((node, index) => {
          treeManager.addNode(node.id, node, ubiId, index);
        });
      }
    }

    // 4. COSTRUZIONE DEL RESTO DEI NODI (Standard Root Level)
    // Ordiniamo anche i nodi standard cronologicamente prima di inserirli
    standardNodes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    standardNodes.forEach((node) => {
      // Usiamo l'indice globale per posizionarli dopo il blocco ESAZIONE
      treeManager.addNode(node.id, node, undefined, rootGlobalIndex++);
    });
  }
}
